import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide for Pledge Sub-subscriber Management page.
 * Targets Add New Subscriber button, search bar, and table.
 */
export const usePledgeSubSubscriberManagementTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnSubSubscriberManagement =
    pathname === ROUTE_NAMES.SUB_SUBSCRIBER_MANAGEMENT;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "pledgeSubSubscriberManagement",
      enabled: isOnSubSubscriberManagement,
      steps: [
        {
          id: "pledge-subsubscriber-add",
          targetSelector: '[data-tour-item="pledge-subsubscriber-add"]',
          title: t("tourGuide.pledgeSubSubscriberManagement.addButton.title"),
          description: t(
            "tourGuide.pledgeSubSubscriberManagement.addButton.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-subsubscriber-search",
          targetSelector: '[data-tour-item="pledge-subsubscriber-search"]',
          title: t("tourGuide.pledgeSubSubscriberManagement.search.title"),
          description: t(
            "tourGuide.pledgeSubSubscriberManagement.search.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-subsubscriber-table",
          targetSelector: '[data-tour-item="pledge-subsubscriber-table"]',
          title: t("tourGuide.pledgeSubSubscriberManagement.table.title"),
          description: t(
            "tourGuide.pledgeSubSubscriberManagement.table.description",
          ),
          position: "top",
          offset: { x: 0, y: -15 },
          highlightPadding: 12,
        },
      ],
    }),
    [isOnSubSubscriberManagement, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            {
              type: TourGuideType.PledgeSubSubscriberManagement,
              isDone: true,
            },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const pledgeSubSubscriberManagementTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.PledgeSubSubscriberManagement,
    isOnTourPath: isOnSubSubscriberManagement && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (pledgeSubSubscriberManagementTourGuide.isActive) {
      setActiveTourModule("pledgeSubSubscriberManagement");
      setCurrentTourStep(pledgeSubSubscriberManagementTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    pledgeSubSubscriberManagementTourGuide.isActive,
    pledgeSubSubscriberManagementTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    pledgeSubSubscriberManagementTourGuide,
    isMobile,
  };
};
